<template>
    <div class="modal-container">
        <form @submit.prevent="handleSubmit" class="modal-form">
            <div class="modal-header">
                <div class="icon-container">
                    <PhPhone :size="24" class="text-gray-500" />
                </div>
                <div class="header-text">
                    <h2>{{ isEditing ? 'Edit' : 'Add' }} Phone Number</h2>
                    <p v-if="isPrimary">Primary phone number for your company</p>
                    <p v-else>Additional phone number</p>
                </div>
            </div>

            <div class="modal-body">
                <div class="form-row">
                    <label>Phone Number</label>
                    <PhoneFields v-model="phoneValue" @update:valid="isPhoneValid = $event" />
                </div>

                <div v-if="!isPrimary" class="form-row">
                    <label>Type</label>
                    <PrimeSelect
                        v-model="formData.type"
                        :options="phoneTypes"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Select type"
                        class="w-full"
                    />
                </div>
            </div>

            <div class="modal-footer">
                <PrimeButton label="Cancel" @click="closeDialog" class="p-btn-root_secondary" :disabled="isLoading" />
                <PrimeButton :label="isEditing ? 'Update' : 'Add'" type="submit" :loading="isLoading" />
            </div>
        </form>
    </div>
</template>

<script setup lang="ts">
import { PhPhone } from '@phosphor-icons/vue';
import PhoneFields, { type PhoneValue } from '~/components/shared/forms/PhoneFields.vue';
import { businessService } from '~/services/business-service';
import { ModalNames } from '~/types/modals';

const dialogRef = getDialogRef(ModalNames.UPDATE_BUSINESS_PHONE);
const { showSuccess, showError } = useGlobalToast();
const isLoading = ref(false);

const formData = reactive({
    country_code: '1',
    phone_number: '',
    type: 'additional',
});

// The shared phone control owns the country list, the digit-only input, the
// formatting and the validity rule; this modal only owns where the value goes.
const isPhoneValid = ref(false);
const phoneValue = computed<PhoneValue>({
    get: () => ({ country_code: formData.country_code, phone_number: formData.phone_number }),
    set: (value) => {
        formData.country_code = value.country_code;
        formData.phone_number = value.phone_number;
    },
});

const phoneTypes = [
    { label: 'Office', value: 'additional' },
    { label: 'Mobile', value: 'mobile' },
    { label: 'Fax', value: 'fax' },
];

const companyId = computed(() => dialogRef.value?.data?.companyId);
const phone = computed(() => dialogRef.value?.data?.phone);
const isPrimary = computed(() => dialogRef.value?.data?.isPrimary || false);
const isEditing = computed(() => !!phone.value?.id);

onMounted(() => {
    if (phone.value) {
        formData.country_code = phone.value.country_code || '1';
        formData.phone_number = phone.value.phone_number || '';
        formData.type = phone.value.type || 'additional';
    }
    if (isPrimary.value) {
        formData.type = 'primary';
    }
});

const closeDialog = () => {
    dialogRef.value.close();
};

const handleSubmit = async () => {
    if (!isPhoneValid.value) {
        showError('Error', 'Please enter a valid phone number');
        return;
    }

    isLoading.value = true;
    try {
        const cleanPhone = formData.phone_number.replace(/\D/g, '');
        const payload = {
            id: phone.value?.id,
            country_code: formData.country_code,
            phone_number: cleanPhone,
            type: isPrimary.value ? 'primary' : formData.type,
        };

        await businessService.savePhoneNumber(companyId.value, payload);
        showSuccess('Success', `Phone number ${isEditing.value ? 'updated' : 'added'}`);
        dialogRef.value.close({ updated: true });
    } catch (error: any) {
        showError('Error', error.message || 'Failed to save phone number');
    } finally {
        isLoading.value = false;
    }
};
</script>

<style lang="scss" scoped>
@use '../../../assets/styles/business-form-modal';
</style>
